import React, { useEffect, useRef } from "react";
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

type TToolbarOptions = (string[] | {
  header: (number | boolean)[];
}[] | {
  list: string;
}[])[]

interface QuillEditorProps {
  content: string;
  onChange: (content: string) => void;
  height?: string | number;
  toolbarOptions?: TToolbarOptions;
  formats?: string[];
}

const QuillEditor = ({
  content,
  onChange,
  height = "400px",
  toolbarOptions = [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'link'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['clean'],
  ],
  formats = [
    'header', 'bold', 'italic', 'underline', 'link',
    'list', 'bullet', 'align', 'color', 'background',
    'indent', 'code-block', 'blockquote',
  ],
}: QuillEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<Quill | null>(null);

  // Initialize Quill
  useEffect(() => {
    if (editorRef.current && !quillInstanceRef.current) {
      // Clear any existing content
      editorRef.current.innerHTML = '';

      // Initialize Quill
      const quill = new Quill(editorRef.current, {
        theme: 'snow',
        modules: {
          toolbar: toolbarOptions,
        },
        formats: formats,
      });

      // Set initial content
      quill.root.innerHTML = content;

      // Set up change handler
      quill.on('text-change', () => {
        onChange(quill.root.innerHTML);
      });

      quillInstanceRef.current = quill;
    }

    return () => {
      if (quillInstanceRef.current) {
        // Clean up Quill instance
        const toolbar = editorRef.current?.parentElement?.querySelector('.ql-toolbar');
        if (toolbar) {
          toolbar.remove();
        }
        quillInstanceRef.current = null;
        if (editorRef.current) {
          editorRef.current.innerHTML = '';
        }
      }
    };
  }, []);

  // Update content if it changes externally
  useEffect(() => {
    if (quillInstanceRef.current && content !== quillInstanceRef.current.root.innerHTML) {
      quillInstanceRef.current.root.innerHTML = content;
    }
  }, [content]);

  return (
    <div
      ref={editorRef}
      style={{ height: height }}
    />
  );
};

export default QuillEditor;
